/**
 * Deciding whether a site changed — the one judgement in this path, and
 * therefore the one part of it that is pure and tested.
 *
 * Everything it refuses to call news is deliberate, and the reasons are the
 * same ones the poller's differ has: the expensive failure is not missing a
 * change, it is marking one that was our own doing.
 */

import type { SiteCursor, SiteObservation } from './types.js';

export interface SiteAdvance {
  cursor: SiteCursor;
  /** Whether to move `changedAt`, which is the only thing a reader ever sees. */
  changed: boolean;
}

/**
 * The first look is never news.
 *
 * Same rule as a null previous snapshot, for the same reason: without it,
 * saving a URL immediately marks the site changed, and the very first thing
 * this feature would ever tell anybody would be wrong.
 *
 * A mode switch is not news either. Discovering a feed on a site we had been
 * hashing is us getting better at watching it, not the author doing anything —
 * and the two cursors are not comparable in any case.
 */
export function advance(
  previous: SiteCursor | null,
  observed: SiteObservation,
  now: Date,
): SiteAdvance {
  const at = now.toISOString();
  const seen = fields(observed);

  if (!previous || previous.mode !== observed.mode) {
    return { cursor: { ...seen, checkedAt: at }, changed: false };
  }

  const changed =
    observed.mode === 'feed'
      ? // An empty feed is a site with nothing published, or a fetch that went
        // wrong quietly. Neither is an announcement.
        Boolean(observed.latestId) && observed.latestId !== previous.latestId
      : Boolean(observed.hash) && observed.hash !== previous.hash;

  if (!changed) {
    // The marker survives a check that found nothing: it means "you have not
    // looked since this changed", and only the reader clears it.
    return { cursor: { ...previous, checkedAt: at }, changed: false };
  }

  return { cursor: { ...seen, changedAt: at, checkedAt: at }, changed: true };
}

function fields(observed: SiteObservation): Omit<SiteCursor, 'checkedAt'> {
  return {
    mode: observed.mode,
    ...(observed.latestId ? { latestId: observed.latestId } : {}),
    ...(observed.latestTitle ? { latestTitle: observed.latestTitle } : {}),
    ...(observed.latestUrl ? { latestUrl: observed.latestUrl } : {}),
    ...(observed.latestAt ? { latestAt: observed.latestAt } : {}),
    ...(observed.hash ? { hash: observed.hash } : {}),
  };
}

/**
 * Whether the reader has seen the current change.
 *
 * Absent `seenAt` means unseen rather than seen, so nothing needs backfilling
 * when someone starts following a site that already has history — the same
 * choice `event_reads` makes.
 */
export function isUnseen(cursor: SiteCursor | null, seenAt: string | null): boolean {
  return changedSince(cursor?.changedAt, seenAt);
}

/**
 * Compared as instants and never as strings. The two sides arrive in different
 * spellings of the same thing — `changedAt` is `toISOString()` from the worker
 * ("…:00.000Z"), `seenAt` is a Postgres timestamptz ("…:00.000000+00:00") — and
 * `'+' < '.'`, so a lexicographic comparison calls a *later* database time
 * earlier and leaves the mark showing forever.
 */
export function changedSince(
  changedAt: string | null | undefined,
  seenAt: string | null | undefined,
): boolean {
  if (!changedAt) return false;
  if (!seenAt) return true;

  const changed = Date.parse(changedAt);
  const seen = Date.parse(seenAt);
  // An unparseable timestamp is our bug, not news. Staying quiet is the right
  // way to be wrong here.
  if (Number.isNaN(changed) || Number.isNaN(seen)) return false;
  return changed > seen;
}
